import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Row, Col, Spinner, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import api from '../helpers/api';

/**
 * Create / edit form for a procurement request (Planning & Purchasing).
 * Uniform two-column (md=6) grid mirroring the Work Orders form. Usage type drives
 * which date fields show; the "new item / new vendor" toggle progressively reveals
 * the extended fields. Suggested Vendor is a directory-backed combobox that also
 * accepts free text; Related Asset is an optional searchable reference.
 *
 * Props:
 *   isOpen, toggle, requestData, onSubmit(formData), isSubmitting,
 *   errors, apiError, locations[], enumData[], orgCurrency
 */
const ProcurementRequestModal = ({
  isOpen,
  toggle,
  requestData,
  onSubmit,
  isSubmitting,
  errors = {},
  apiError = '',
  locations = [],
  enumData = [],
  orgCurrency = '',
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [vendors, setVendors] = useState([]);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: '#86b7fe' },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': { backgroundColor: '#2e669a' },
    }),
  };

  const requestTypeOptions = [
    { value: 'ASSET', label: t('planning_purchasing.type_asset', 'Asset') },
    { value: 'PARTS_CONSUMABLES', label: t('planning_purchasing.type_parts_consumables', 'Parts & Consumables') },
  ];

  const usageTypeOptions = [
    { value: 'PERMANENT', label: t('planning_purchasing.usage_permanent', 'Permanent') },
    { value: 'TEMPORARY', label: t('planning_purchasing.usage_temporary', 'Temporary') },
  ];

  const unitOptions = (enumData?.find((e) => e.name === 'unit')?.values || []).map((val) => ({
    value: val,
    label: val,
  }));

  const priorityOptions = (enumData?.find((e) => e.name === 'urgencyLevel')?.values || []).map((val) => ({
    value: val,
    label: t(`shared.${val.toLowerCase()}`, val.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())),
  }));

  const locationOptions = locations.map((loc) => ({ value: loc.id, label: loc.locationName }));

  const vendorOptions = vendors.map((v) => ({ value: v.id, label: v.vendorName }));

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setUploadError('');
      setFormData({
        id: requestData?.id || null,
        requestType: requestData?.requestType || 'ASSET',
        title: requestData?.title || '',
        quantity: requestData?.quantity ?? 1,
        unit: requestData?.unit || null,
        urgencyLevel: requestData?.urgencyLevel || null,
        locationId: requestData?.locationId || null,
        assetId: requestData?.assetId || null,
        assetName: requestData?.assetName || '',
        usageType: requestData?.usageType || 'PERMANENT',
        requiredByDate: requestData?.requiredByDate || '',
        startDate: requestData?.startDate || '',
        endDate: requestData?.endDate || '',
        notes: requestData?.notes || '',
        newItemFlag: requestData?.newItemFlag || false,
        manufacturer: requestData?.manufacturer || '',
        modelReference: requestData?.modelReference || '',
        suggestedVendor: requestData?.suggestedVendor || '',
        suggestedVendorId: requestData?.suggestedVendorId || null,
        estimatedPrice: requestData?.estimatedPrice ?? '',
        estimatedPriceIncludesTax: requestData?.estimatedPriceIncludesTax || false,
        currency: requestData?.currency || orgCurrency || '',
        attachments: requestData?.attachments || [],
      });
    }
  }, [isOpen, requestData, orgCurrency]);

  // Vendor directory for the Suggested Vendor combobox
  useEffect(() => {
    if (!isOpen) return;
    api.get('vendor/getAllVendors?count=9999')
      .then(({ data }) => setVendors(data?.response || []))
      .catch((e) => console.error('vendors load error', e));
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Related-asset search (same lightweight endpoint as ParentAssetSelector)
  const loadAssetOptions = useCallback(async (search = '') => {
    try {
      const params = { count: 10 };
      if (search) params.search = search;
      const { data } = await api.get('asset/get-assets-info', { params });
      return (data?.response || []).map((a) => ({
        value: a.id,
        label: a.assetName,
        serialNumber: a.serialNumber,
      }));
    } catch (error) {
      console.error('Error searching assets:', error);
      return [];
    }
  }, []);

  const formatAssetOption = (option) => (
    <div>
      <div className="fw-medium">{option.label}</div>
      {option.serialNumber && <small className="text-muted">{option.serialNumber}</small>}
    </div>
  );

  // Suggested Vendor combobox: directory match stores the id, free text clears it
  const vendorValue = formData.suggestedVendorId
    ? vendorOptions.find((o) => o.value === formData.suggestedVendorId)
      || { value: formData.suggestedVendorId, label: formData.suggestedVendor }
    : formData.suggestedVendor
      ? { value: null, label: formData.suggestedVendor }
      : null;

  const onVendorChange = (opt) => {
    if (!opt) {
      setFormData((prev) => ({ ...prev, suggestedVendor: '', suggestedVendorId: null }));
    } else if (opt.__isNew__) {
      setFormData((prev) => ({ ...prev, suggestedVendor: opt.label, suggestedVendorId: null }));
    } else {
      setFormData((prev) => ({ ...prev, suggestedVendor: opt.label, suggestedVendorId: opt.value }));
    }
  };

  // Attachments — images and documents upload independently so one failing
  // batch doesn't abort the other; failures surface in the modal.
  const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // matches the "max 10 MB each" hint

  const handleAttachmentUpload = async (e) => {
    const all = Array.from(e.target.files);
    if (all.length === 0) return;
    const files = all.filter((f) => f.size <= MAX_ATTACHMENT_BYTES);
    const oversized = all.length - files.length;
    setUploading(true);
    setUploadError(oversized > 0
      ? t('planning_purchasing.attach_too_large', '{{n}} file(s) exceed the 10 MB limit and were not attached.', { n: oversized })
      : '');
    if (files.length === 0) {
      setUploading(false);
      e.target.value = '';
      return;
    }
    const images = files.filter((f) => f.type.startsWith('image/'));
    const docs = files.filter((f) => !f.type.startsWith('image/'));
    const urls = [];
    let failed = 0;

    const uploadBatch = async (batch, endpoint) => {
      if (batch.length === 0) return;
      try {
        const fd = new FormData();
        batch.forEach((f) => fd.append('files', f));
        const { data } = await api.post(endpoint, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        (data.response || []).forEach((item) => urls.push(item.publicUrl));
      } catch (error) {
        console.error(`Error uploading via ${endpoint}:`, error);
        failed += batch.length;
      }
    };

    await uploadBatch(images, 'files/img-upload');
    await uploadBatch(docs, 'files/pdf-upload');

    if (urls.length > 0) {
      setFormData((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...urls] }));
    }
    if (failed > 0) {
      const failedMsg = t('planning_purchasing.upload_failed', '{{n}} file(s) could not be uploaded. Please try again.', { n: failed });
      setUploadError((prev) => (prev ? `${prev} ${failedMsg}` : failedMsg));
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isEditMode = !!formData.id;
  const isTemporary = formData.usageType === 'TEMPORARY';

  const isImage = (url) => /\.(png|jpe?g|gif|webp)$/i.test(url || '');

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">
              {isEditMode
                ? t('planning_purchasing.edit_request', 'Edit Request')
                : t('planning_purchasing.new_request', 'New Request')}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={toggle} aria-label={t('shared.close', 'Close')} />
          </div>

          <div className="modal-body p-4">
            {apiError && <Alert color="danger" className="mb-4">{apiError}</Alert>}

            {/* Request Type + Title */}
            <Row className="mb-4">
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('planning_purchasing.request_type', 'Request Type')} <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={requestTypeOptions}
                    value={requestTypeOptions.find((o) => o.value === formData.requestType) || null}
                    onChange={(s) => handleChange('requestType', s?.value || null)}
                    placeholder={t('planning_purchasing.select_request_type', 'Select type')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.requestType && <small className="text-danger">{errors.requestType}</small>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('shared.title', 'Title')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    value={formData.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder={t('planning_purchasing.title_placeholder', 'What do you need?')}
                  />
                  {errors.title && <small className="text-danger">{errors.title}</small>}
                </div>
              </Col>
            </Row>

            {/* Quantity + Unit */}
            <Row className="mb-4">
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('planning_purchasing.quantity', 'Quantity')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
                    value={formData.quantity ?? 1}
                    onChange={(e) => handleChange('quantity', e.target.value === '' ? '' : Number(e.target.value))}
                  />
                  {errors.quantity && <small className="text-danger">{errors.quantity}</small>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('planning_purchasing.unit', 'Unit')}</label>
                  <Select
                    options={unitOptions}
                    value={unitOptions.find((o) => o.value === formData.unit) || null}
                    onChange={(s) => handleChange('unit', s?.value || null)}
                    isClearable
                    placeholder={t('planning_purchasing.select_unit', 'Select unit')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
            </Row>

            {/* Priority + Related Asset */}
            <Row className="mb-4">
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('shared.priority', 'Priority')}</label>
                  <Select
                    options={priorityOptions}
                    value={priorityOptions.find((o) => o.value === formData.urgencyLevel) || null}
                    onChange={(s) => handleChange('urgencyLevel', s?.value || null)}
                    isClearable
                    placeholder={t('shared.select_priority', 'Select Priority')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('planning_purchasing.related_asset', 'Related Asset')}</label>
                  <AsyncSelect
                    defaultOptions
                    loadOptions={loadAssetOptions}
                    value={formData.assetId ? { value: formData.assetId, label: formData.assetName } : null}
                    onChange={(opt) => setFormData((prev) => ({ ...prev, assetId: opt?.value || null, assetName: opt?.label || '' }))}
                    formatOptionLabel={formatAssetOption}
                    isClearable
                    isSearchable
                    placeholder={t('shared.type_to_search_assets', 'Type to search assets...')}
                    noOptionsMessage={() => t('shared.no_assets_found', 'No assets found')}
                    loadingMessage={() => t('shared.loading', 'Loading...')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
            </Row>

            {/* Location + Usage type */}
            <Row className="mb-4">
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('planning_purchasing.target_location', 'Target Location')} <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={locationOptions}
                    value={locationOptions.find((o) => o.value === formData.locationId) || null}
                    onChange={(s) => handleChange('locationId', s?.value || null)}
                    isClearable
                    isSearchable
                    placeholder={t('shared.search_and_select_location', 'Search and select location...')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                  {errors.locationId && <small className="text-danger">{errors.locationId}</small>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('planning_purchasing.usage_type', 'Usage Type')} <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={usageTypeOptions}
                    value={usageTypeOptions.find((o) => o.value === formData.usageType) || null}
                    onChange={(s) => handleChange('usageType', s?.value || null)}
                    placeholder={t('planning_purchasing.select_usage_type', 'Select usage type')}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
              </Col>
            </Row>

            {/* Conditional date fields */}
            <Row className="mb-4">
              {!isTemporary && (
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label fw-medium">
                      {t('planning_purchasing.required_by_date', 'Required By')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.requiredByDate ? 'is-invalid' : ''}`}
                      value={formData.requiredByDate || ''}
                      onChange={(e) => handleChange('requiredByDate', e.target.value)}
                    />
                    {errors.requiredByDate && <small className="text-danger">{errors.requiredByDate}</small>}
                  </div>
                </Col>
              )}
              {isTemporary && (
                <>
                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label fw-medium">
                        {t('planning_purchasing.start_date', 'Start Date')} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                        value={formData.startDate || ''}
                        onChange={(e) => handleChange('startDate', e.target.value)}
                      />
                      {errors.startDate && <small className="text-danger">{errors.startDate}</small>}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-group">
                      <label className="form-label fw-medium">
                        {t('planning_purchasing.end_date', 'End Date')} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                        value={formData.endDate || ''}
                        onChange={(e) => handleChange('endDate', e.target.value)}
                      />
                      {errors.endDate && <small className="text-danger">{errors.endDate}</small>}
                    </div>
                  </Col>
                </>
              )}
            </Row>

            {/* Notes */}
            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('planning_purchasing.notes', 'Additional Notes')}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    maxLength={1000}
                    value={formData.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder={t('planning_purchasing.notes_placeholder', 'Any additional details...')}
                  />
                </div>
              </Col>
            </Row>

            {/* New item / new vendor toggle */}
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="newItemFlag"
                checked={!!formData.newItemFlag}
                onChange={(e) => handleChange('newItemFlag', e.target.checked)}
              />
              <label className="form-check-label fw-medium" htmlFor="newItemFlag">
                {t('planning_purchasing.new_item_vendor', 'New item / new vendor')}
              </label>
            </div>

            {formData.newItemFlag && (
              <div className="border rounded p-3 mb-4 bg-light">
                <Row className="mb-3">
                  <Col md={6}>
                    <label className="form-label fw-medium">{t('planning_purchasing.manufacturer', 'Manufacturer')}</label>
                    <input type="text" className="form-control" value={formData.manufacturer || ''} onChange={(e) => handleChange('manufacturer', e.target.value)} />
                  </Col>
                  <Col md={6}>
                    <label className="form-label fw-medium">{t('planning_purchasing.model_reference', 'Model / Reference')}</label>
                    <input type="text" className="form-control" value={formData.modelReference || ''} onChange={(e) => handleChange('modelReference', e.target.value)} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <label className="form-label fw-medium">{t('planning_purchasing.suggested_vendor', 'Suggested Vendor')}</label>
                    <CreatableSelect
                      options={vendorOptions}
                      value={vendorValue}
                      onChange={onVendorChange}
                      isClearable
                      placeholder={t('planning_purchasing.vendor_select_or_type', 'Pick a vendor or type a new name')}
                      formatCreateLabel={(input) => `${t('planning_purchasing.use_vendor_name', 'Use vendor name')}: "${input}"`}
                      classNamePrefix="react-select"
                      styles={selectStyles}
                    />
                  </Col>
                  <Col md={6}>
                    <label className="form-label fw-medium">{t('planning_purchasing.estimated_price', 'Estimated Unit Price')}</label>
                    <input type="number" step="0.01" min="0" className="form-control" value={formData.estimatedPrice ?? ''} onChange={(e) => handleChange('estimatedPrice', e.target.value === '' ? '' : Number(e.target.value))} />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <label className="form-label fw-medium">{t('planning_purchasing.currency', 'Currency')}</label>
                    <input type="text" maxLength={3} className="form-control text-uppercase" value={formData.currency || ''} onChange={(e) => handleChange('currency', e.target.value.toUpperCase())} />
                  </Col>
                  <Col md={6}>
                    <label className="form-label fw-medium d-block">
                      {t('planning_purchasing.price_includes_tax', 'Estimated price includes tax')}
                    </label>
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="priceInclTax"
                        checked={!!formData.estimatedPriceIncludesTax}
                        onChange={(e) => handleChange('estimatedPriceIncludesTax', e.target.checked)}
                      />
                      <label className="form-check-label small text-muted" htmlFor="priceInclTax">
                        {t('planning_purchasing.price_tax_hint', 'Tells the reviewer whether tax is already included in your estimate')}
                      </label>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Attachments */}
            <Row className="mb-2">
              <Col md={12}>
                <div className="form-group">
                  <label className="form-label fw-medium">{t('planning_purchasing.attachments', 'Attachments')}</label>
                  {uploadError && <Alert color="warning" className="py-2 mb-2">{uploadError}</Alert>}
                  <div className="border rounded p-4 text-center" style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}>
                    <input
                      type="file"
                      id="procAttachInput"
                      className="d-none"
                      accept="image/png,image/jpeg,application/pdf"
                      multiple
                      onChange={handleAttachmentUpload}
                      disabled={uploading}
                    />
                    <label htmlFor="procAttachInput" className={`btn btn-outline-primary mb-2 ${uploading ? 'disabled' : ''}`} style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                      {uploading ? (
                        <><Spinner size="sm" className="me-2" />{t('shared.uploading', 'Uploading...')}</>
                      ) : (
                        <><FeatherIcon icon="paperclip" size={16} className="me-2" />{t('planning_purchasing.attach_files', 'Attach Files')}</>
                      )}
                    </label>
                    <p className="text-muted small mb-0">{t('planning_purchasing.attach_hint', 'PDF, JPG or PNG — max 10 MB each')}</p>
                  </div>
                  {formData.attachments?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {formData.attachments.map((url, index) => (
                        <div key={index} className="d-flex align-items-center gap-2 bg-light rounded p-2 pe-3">
                          <FeatherIcon icon={isImage(url) ? 'image' : 'file'} size={16} className="text-muted" />
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-decoration-none small">
                            {t('shared.document', 'Document')} {index + 1}
                          </a>
                          <button type="button" className="btn btn-link btn-sm p-0 text-danger ms-2" onClick={() => removeAttachment(index)}>
                            <FeatherIcon icon="x" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button type="button" className="btn btn-outline-secondary" onClick={toggle}>
              {t('shared.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting || uploading}>
              {isSubmitting ? (
                <><Spinner size="sm" className="me-2" />{isEditMode ? t('shared.updating', 'Updating...') : t('shared.submitting', 'Submitting...')}</>
              ) : (
                isEditMode ? t('planning_purchasing.update_request', 'Update Request') : t('planning_purchasing.create_request', 'Create Request')
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </Modal>
  );
};

export default ProcurementRequestModal;
